// React / Next
import { Link } from '@tanstack/react-router';

// Components
import { Typography } from '@archieai/ui';

// Utils
import { cn } from '../../utils/cn';

// Types
type BlogCardProps = {
  /** URL of the cover image */
  imageSrc: string;
  /** Alt text for the cover image */
  imageAlt?: string;
  /** Title of the blog post */
  title: string;
  /** Short excerpt/description of the blog post */
  excerpt: string;
  /** Author name */
  author: string;
  /** Tags or categories, pipe-separated (e.g., "AI Strategy | AI Adoption") */
  tags: string;
  /** Link to the full blog post */
  href?: string;
  /** Additional className for custom styling */
  className?: string;
  /** Optional click handler for analytics tracking */
  onClick?: () => void;
};

export const BlogCard = ({
  imageSrc,
  imageAlt = '',
  title,
  excerpt,
  author,
  tags,
  href,
  className,
  onClick,
}: BlogCardProps) => {
  const cardContent = (
    <>
      {/* Cover Image */}
      <div className="w-full aspect-[323/243] overflow-hidden shrink-0">
        <img
          src={imageSrc}
          alt={imageAlt}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Content */}
      <div className="flex flex-col gap-[var(--spacing-8)] p-[var(--spacing-24)] flex-1">
        {/* Title - clamp to 2 lines */}
        <Typography as="h3" variant="h4" className="text-blue-1000 line-clamp-2">
          {title}
        </Typography>

        {/* Excerpt - clamp to 3 lines, grows to fill space */}
        <Typography as="p" variant="tiny" className="text-blue-1000 line-clamp-3 flex-1">
          {excerpt}
        </Typography>

        {/* Author */}
        <p className="font-heading text-[16px] leading-[28px] font-light text-blue-800">
          {author}
        </p>

        {/* Tags - clamp to 1 line */}
        <Typography variant="x-small" className="text-blue-800 line-clamp-1">
          {tags}
        </Typography>
      </div>
    </>
  );

  const baseClasses = cn(
    'flex flex-col bg-white border border-gray-200 rounded-12 overflow-hidden transition-shadow duration-200 hover:shadow-medium',
    'w-full h-full',
    className
  );

  if (href) {
    return (
      <Link
        to={href}
        className={baseClasses}
        aria-label={`Read more: ${title}`}
        onClick={onClick}
      >
        {cardContent}
      </Link>
    );
  }

  return (
    <article className={baseClasses}>
      {cardContent}
    </article>
  );
};

